import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import CenteredLoading from "./CenteredLoading";
import { faPlay, faPause, faReply } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const msToSeconds = (ms: number): number => Math.round(ms / 1000);

export function useSpotifyEmbed(): {
  onControllerReady: (controller: any) => void;
  isPaused: boolean | undefined;
  isBuffering: boolean | undefined;
  duration: number | undefined;
  position: number | undefined;
  isReady: boolean;
  togglePlayPause: () => void;
  seekBackward: () => void;
  seekToBeginning: () => void;
  seekForward: () => void;
} {
  console.log("useSpotifyEmbedFiring");
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [isBuffering, setIsBuffering] = useState<boolean>(false);
  const [duration, setDuration] = useState<number | undefined>(undefined);
  const [position, setPosition] = useState<number | undefined>(undefined);

  const controllerRef = useRef<any>(null);

  const togglePlayPause = useCallback(() => {
    controllerRef.current.togglePlay();
  }, []);

  const seekBackward = useCallback(() => {
    if (!controllerRef.current || position === undefined) {
      throw new Error("this shouldn't happen");
    }
    controllerRef.current.seek(Math.max(0, position - 5));
  }, [position]);

  const seekToBeginning = useCallback(() => {
    if (!controllerRef.current) {
      throw new Error("this shouldn't happen");
    }
    controllerRef.current.restart();
  }, []);

  const seekForward = useCallback(() => {
    if (
      !controllerRef.current ||
      position === undefined ||
      duration === undefined
    ) {
      throw new Error("this shouldn't happen");
    }
    controllerRef.current.seek(Math.min(duration, position + 5));
  }, [position, duration]);

  const onControllerReady = useCallback((controller: any) => {
    let lastDuration: number | undefined = undefined;
    controller.addListener(
      "playback_update",
      (e: {
        data: {
          isPaused: boolean;
          isBuffering: boolean;
          duration: number;
          position: number;
        };
      }) => {
        controllerRef.current = controller;
        setIsPaused(e.data.isPaused);
        setIsBuffering(e.data.isBuffering);
        setPosition(msToSeconds(e.data.position));
        const durationSeconds = msToSeconds(e.data.duration);
        if (durationSeconds !== lastDuration) {
          setDuration(durationSeconds);
        }
      }
    );
  }, []);

  return {
    isReady:
      isPaused !== undefined &&
      isBuffering !== undefined &&
      duration !== undefined &&
      position !== undefined,
    isPaused,
    isBuffering,
    duration,
    position,
    onControllerReady,
    togglePlayPause,
    seekBackward,
    seekForward,
    seekToBeginning,
  };
}

interface SpotifyEmbedProps {
  trackId: string;
  onControllerReady: (controller: any) => void;
}

const SpotifyEmbed: React.FC<SpotifyEmbedProps> = ({
  trackId,
  onControllerReady,
}) => {
  const embedRef = useRef<HTMLDivElement | null>(null);
  const controllerRef = useRef<any>(null);

  useEffect(() => {
    if (!embedRef.current) {
      console.error("this shouldn't happen");
      return;
    }

    (window as any).onSpotifyIframeApiReady = (IFrameAPI: any) => {
      IFrameAPI.createController(
        embedRef.current,
        {
          uri: `spotify:track:${trackId}`,
        },
        onControllerReady
      );
    };

    const IFRAME_ELEMENT_ID = "spotify-iframe-api";
    if (document.getElementById(IFRAME_ELEMENT_ID)) {
      console.log("have already injected iframe into page!");
      return;
    }

    const script = document.createElement("script");
    script.id = IFRAME_ELEMENT_ID;
    script.src = "https://open.spotify.com/embed/iframe-api/v1";
    script.async = true;
    document.body.appendChild(script);
  }, [trackId]);

  return (
    <div>
      <div id="embed-iframe" ref={embedRef} />
    </div>
  );
};

export default SpotifyEmbed;
